import {
  ToolbarEmbeddedMoreActions,
  ToolbarIconButton,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  ToolbarPopoverButton,
  ToolbarPopoverSwitchRow,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { ReactNode } from "react";

export interface MapCameraActions {
  onShowWholeMap: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** The current map's region in a sentence, e.g. "mainland China". */
  regionName: string;
}

/**
 * The map's More menu, a circle over the sheet's left edge (the camera buttons ride its right):
 * view settings (e.g. ignore locked regions), offline map downloads, the zoom buttons (the only way
 * to zoom without a touchscreen) and framing shortcuts. The app shell's top-left slot holds the
 * Back control that returns to the cards, so the menu lives in the map's own content.
 */
export function MapMoreActions({
  camera,
  offlineMaps,
}: {
  /** Absent until a map has loaded: there is nothing to zoom or frame yet. */
  camera: MapCameraActions | null;
  /** The Offline maps section (`useMapOfflineDownloads`), kept by the page shell across maps. */
  offlineMaps: ReactNode;
}) {
  const [disableLockedAreas, setDisableLockedAreas] = useDeviceSetting("mapDisableLockedAreas");
  return (
    <ToolbarEmbeddedMoreActions title="Map">
      <ToolbarPopoverButton
        accessibilityLabel="Map settings"
        label="Map"
        name="MdTune"
        selected={disableLockedAreas}
        testID="map-settings-button"
      >
        <ToolbarPopoverSwitchRow
          checked={disableLockedAreas}
          description="Ignore taps on places you have not unlocked, so unlocked regions are easier to hit"
          label="Disable locked areas"
          onCheckedChange={setDisableLockedAreas}
          testID="map-disable-locked-areas"
        />
      </ToolbarPopoverButton>
      {offlineMaps}
      {camera === null ? null : (
        <>
          <ToolbarIconButton
            accessibilityLabel="Zoom in"
            label="Zoom in"
            name="MdAdd"
            onPress={camera.onZoomIn}
            testID="map-zoom-in-button"
          />
          <ToolbarIconButton
            accessibilityLabel="Zoom out"
            label="Zoom out"
            name="MdRemove"
            onPress={camera.onZoomOut}
            testID="map-zoom-out-button"
          />
          <ToolbarIconButton
            accessibilityLabel={`Show all of ${camera.regionName}`}
            label={`Show all of ${camera.regionName}`}
            name="MdZoomOutMap"
            onPress={camera.onShowWholeMap}
            testID="map-show-china-button"
          />
        </>
      )}
    </ToolbarEmbeddedMoreActions>
  );
}
